<template>
  <v-container class="py-6" style="max-width: 680px;">
    <div id="top"></div>
    <h1 class="text-h5 font-weight-bold mb-2">Profilul tău</h1>

    <!-- T0: not started -->
    <template v-if="profileState === 'T0'">
      <v-alert v-if="pendingInvites.length" type="info" variant="tonal" rounded="lg" class="mb-4">
        {{ inviteHeadsUp }}
      </v-alert>
      <p class="text-medium-emphasis mb-6">{{ t0CTABody }}</p>
      <v-btn color="success" size="large" to="/profile/t1" append-icon="mdi-arrow-right">
        Descoperă-ți profilul
      </v-btn>
    </template>

    <!-- T1 or T2: show T1 interpretation -->
    <template v-else>

      <!-- First-match nudge: shown only when T2 is complete and no match activity exists yet -->
      <v-alert
        v-if="profileState === 'T2' && matches.length === 0"
        type="success"
        variant="tonal"
        rounded="lg"
        class="mb-4"
      >
        Felicitări, profilul tău este acum complet! Dacă dorești, poți trimite o cerere de compatibilitate unui coleg, amic sau partener.
        <template #append>
          <v-btn size="small" variant="tonal" color="success" to="/matches">Trimite</v-btn>
        </template>
      </v-alert>

      <!-- Pending invite banner (T2 only — T0/T1 invite copy is woven into their CTA sections) -->
      <v-alert
        v-if="profileState === 'T2' && pendingInvites.length"
        type="info"
        variant="tonal"
        rounded="lg"
        class="mb-4"
      >
        {{ inviteHeadsUp }} Dacă vrei, poți vedea compatibilitatea voastră.
        <template #append>
          <v-btn size="small" variant="tonal" color="info" to="/matches">Vezi invitațiile</v-btn>
        </template>
      </v-alert>

      <!-- Table of contents intro — only shown once T1 results are ready -->
      <template v-if="t1Interpretation">
        <p class="text-body-2 text-medium-emphasis mb-3">
          Profilul tău te descrie din mai multe perspective. Acest raport conține următoarele secțiuni:
        </p>
        <ol class="toc mb-6">
          <li><a href="#section-valori" @click.prevent="scrollTo('section-valori')">Valori</a></li>
          <li><a href="#section-personalitate" @click.prevent="scrollTo('section-personalitate')">Personalitate</a></li>
          <li v-if="profileState === 'T2' && t2Interpretation"><a href="#section-attachment" @click.prevent="scrollTo('section-attachment')">Atașament</a></li>
          <li v-if="profileState === 'T2' && t2Interpretation"><a href="#section-comunicare" @click.prevent="scrollTo('section-comunicare')">Comunicare</a></li>
        </ol>
      </template>

      <template v-if="t1Loading">
        <p class="text-medium-emphasis mb-2">Se generează... Generarea ar putea dura până la 2 minute.</p>
        <v-progress-linear color="success" indeterminate rounded height="6" class="mb-6" />
      </template>
      <template v-else-if="t1Interpretation">

        <!-- VALUES -->
        <div id="section-valori" class="section-heading mb-1">
          <h2 class="text-subtitle-1 font-weight-bold">1. Valori</h2>
          <a href="#top" class="back-to-top text-caption" @click.prevent="scrollTo('top')">↑ sus</a>
        </div>
        <p class="text-body-2 text-medium-emphasis mb-4">Ce contează cu adevărat pentru tine în viață. Valorile tale arată ce te motivează și cum îți prioritizezi deciziile.</p>

        <v-card rounded="lg" elevation="1" class="pa-5 mb-4">
          <div class="d-flex align-center justify-space-between mb-1" style="cursor: pointer" @click="sectionLegend.values = !sectionLegend.values">
            <div class="text-overline text-medium-emphasis">Ierarhia valorilor</div>
            <v-icon size="small" :icon="sectionLegend.values ? 'mdi-help-circle' : 'mdi-help-circle-outline'" />
          </div>
          <v-expand-transition>
            <div v-if="sectionLegend.values" class="text-caption text-medium-emphasis mb-3">
              Ierarhia ta personală a celor 10 valori fundamentale din modelul motivațional Schwartz.
              <ol style="padding-left: 1.25rem;">
                <li><span class="font-weight-bold">Bunăvoință</span> — grijă față de cei apropiați, loialitate, dăruire</li>
                <li><span class="font-weight-bold">Universalism</span> — înțelegere, toleranță, dreptate, protecția naturii</li>
                <li><span class="font-weight-bold">Autodirecționare</span> — libertate de gândire, creativitate, stabilirea propriei direcții</li>
                <li><span class="font-weight-bold">Stimulare</span> — aventură, noutate, căutarea unei vieți captivante</li>
                <li><span class="font-weight-bold">Hedonism</span> — plăcere, bucurie, satisfacție personală</li>
                <li><span class="font-weight-bold">Realizare</span> — succes personal, competență, ambiție</li>
                <li><span class="font-weight-bold">Putere</span> — statut social, prestigiu, control</li>
                <li><span class="font-weight-bold">Securitate</span> — siguranță, stabilitate, ordine socială</li>
                <li><span class="font-weight-bold">Conformitate</span> — respectarea regulilor, evitarea comportamentelor inacceptabile</li>
                <li><span class="font-weight-bold">Tradiție</span> — respect pentru obiceiuri și tradiții, umilință</li>                
              </ol>
            </div>
          </v-expand-transition>
          <div style="height: 280px;">
            <PvqChart :value-ranking="t1Interpretation.local.pvq.valueRanking" />
          </div>
        </v-card>

        <v-card rounded="lg" elevation="1" class="pa-5 mb-4">
          <div class="d-flex align-center justify-space-between mb-1" style="cursor: pointer" @click="sectionLegend.bipolarProfile = !sectionLegend.bipolarProfile">
            <div class="text-overline text-medium-emphasis">Profil valoric bipolar</div>
            <v-icon size="small" :icon="sectionLegend.bipolarProfile ? 'mdi-help-circle' : 'mdi-help-circle-outline'" />
          </div>
          <v-expand-transition>
            <div v-if="sectionLegend.bipolarProfile" class="text-caption text-medium-emphasis mb-3">
              Cele 10 valori Schwartz se grupează în <b>4 dimensiuni</b> așezate pe două axe:
              <ol style="padding-left: 1.25rem;">
                <li>Axa Autodepășire - Autopromovare</li>
                <li>Axa Deschidere - Conservare</li>
              </ol>
              Cu cât te duci spre o dimensiune, pe o axă, cu atât te depărtezi de polul opus de pe acea axă.

              
              <div class="mt-2">Când se însumează toate dimensiunile tale se obține o direcție dominantă care te definește la nivel global.</div>
              
              <div class="mt-2">Acul (din grafic) arată <b>direcția dominantă</b> a profilului tău pe roata valorilor.</div>
              <ul style="padding-left: 1.25rem;">
                <li><span class="font-weight-medium">Autodepășire</span> — prioritizezi bunăstarea celorlalți și echitatea (Bunăvoință + Universalism)</li>
                <li><span class="font-weight-medium">Autopromovare</span> — prioritizezi succesul personal și statutul (Realizare + Putere)</li>
                <li><span class="font-weight-medium">Deschidere</span> — prețuiești independența, noutatea și plăcerea (Autodirecționare + Stimulare + Hedonism)</li>
                <li><span class="font-weight-medium">Conservare</span> — prețuiești ordinea, siguranța și tradiția (Securitate + Tradiție + Conformitate)</li>
              </ul>
            </div>
          </v-expand-transition>
          
          <div style="height: 340px; margin-top: 16px;">
            <PvqWheelChart 
              :value-ranking="t1Interpretation.local.pvq.valueRanking" 
              :angle="wheelChartAngle"
            />
          </div>

          
          <span class="text-caption text-medium-emphasis">Tu ești orientat către</span>: 
          <div v-for="(profile, dim) in t1Interpretation.local.pvq.higherOrderProfile"
              :key="dim"
              class="text-body-2 mb-1"
            >
              <v-chip size="small" color="primary" variant="tonal">{{ HIGHER_ORDER_LABEL[profile.dominant] }}</v-chip>
          </div>          
          
        </v-card>

        <v-card v-if="t1Interpretation.ai?.pvq" rounded="lg" elevation="1" class="pa-5 mb-4" style="position: relative; overflow: hidden;">
          <BotanicalIcon name="flower" :size="64" :opacity="0.18" :rotate="15"
            style="position: absolute; top: 8px; right: 8px; color: rgb(var(--v-theme-primary));" />
          <div class="text-overline text-medium-emphasis mb-2">Valori · Interpretare</div>
          <div class="text-body-2 md-content" v-html="md(t1Interpretation.ai.pvq)"></div>
        </v-card>

        <!-- PERSONALITY -->
        <v-divider class="my-6" />
        <div id="section-personalitate" class="section-heading mb-1">
          <h2 class="text-subtitle-1 font-weight-bold">2. Personalitate</h2>
          <a href="#top" class="back-to-top text-caption" @click.prevent="scrollTo('top')">↑ sus</a>
        </div>
        <p class="text-body-2 text-medium-emphasis mb-4">Trăsăturile tale stabile de caracter — cum ești în general cu oamenii, la muncă și în situații noi.</p>
        <v-card rounded="lg" elevation="1" class="pa-5 mb-4">
          <div class="d-flex align-center justify-space-between mb-1" style="cursor: pointer" @click="sectionLegend.personality = !sectionLegend.personality">
            <div class="text-overline text-medium-emphasis">Personalitate</div>
            <v-icon size="small" :icon="sectionLegend.personality ? 'mdi-help-circle' : 'mdi-help-circle-outline'" />
          </div>
          <v-expand-transition>
            <div v-if="sectionLegend.personality" class="text-caption text-medium-emphasis mb-3">
              Cele 5 trăsături stabile de personalitate din modelul Big Five, măsurate prin TIPI sunt:
              <ul style="padding-left: 1.25rem;">
                <li><span class="font-weight-bold">Extraversiune</span> — sociabilitate, energie, asertivitate</li>
                <li><span class="font-weight-bold">Agreabilitate</span> — cooperare, empatie, orientare spre ceilalți</li>
                <li><span class="font-weight-bold">Conștiinciozitate</span> — organizare, disciplină, fiabilitate</li>
                <li><span class="font-weight-bold">Stabilitate emoțională</span> — calm, rezistență la stres, echilibru</li>
                <li><span class="font-weight-bold">Deschidere</span> — creativitate, curiozitate, interes pentru experiențe noi</li>
              </ul>
            </div>
          </v-expand-transition>
          <div style="height: 300px; margin-bottom: 16px;">
            <TipiChart :dimension-scores="t1Interpretation.local.tipi.dimensionScores" />
          </div>
          <div class="mb-2">
            <span class="text-caption text-medium-emphasis">Trăsătură dominantă: </span> <v-chip size="small" color="primary" variant="tonal">{{ TIPI_LABEL[t1Interpretation.local.tipi.dominantTrait] }}</v-chip>
          </div>
          <div v-if="t1Interpretation.local.tipi.strongTraits.length" class="d-flex align-center flex-wrap gap-2 mb-2">
            <span class="text-caption text-medium-emphasis">Trăsături puternice:</span>
            <v-chip v-for="trait in t1Interpretation.local.tipi.strongTraits" :key="trait" size="small" color="primary" variant="tonal">{{ TIPI_LABEL[trait] }}</v-chip>
          </div>
          <div v-if="t1Interpretation.local.tipi.weakTraits.length" class="d-flex align-center flex-wrap gap-2">
            <span class="text-caption text-medium-emphasis">Trăsături slabe:</span>
            <v-chip v-for="trait in t1Interpretation.local.tipi.weakTraits" :key="trait" size="small" color="secondary" variant="tonal">{{ TIPI_LABEL[trait] }}</v-chip>
          </div>
        </v-card>

        <v-card v-if="t1Interpretation.ai?.tipi" rounded="lg" elevation="1" class="pa-5 mb-4" style="position: relative; overflow: hidden;">
          <BotanicalIcon name="fern" :size="64" :opacity="0.18" :rotate="-12"
            style="position: absolute; top: 8px; right: 8px; color: rgb(var(--v-theme-primary));" />
          <div class="text-overline text-medium-emphasis mb-2">Personalitate · Interpretare</div>
          <div class="text-body-2 md-content" v-html="md(t1Interpretation.ai.tipi)"></div>
        </v-card>

      </template>
      <template v-else-if="t1Failed">
        <v-alert type="error" variant="tonal" class="mb-4">
          Generarea profilului a eșuat.
        </v-alert>
        <v-btn
          color="success"
          variant="tonal"
          prepend-icon="mdi-refresh"
          :loading="t1Retrying"
          @click="retryInterpretation('t1')"
        >
          Încearcă din nou
        </v-btn>
      </template>
      <template v-else>
        <p class="text-medium-emphasis mb-2">Se procesează profilul tău... Generarea ar putea dura până la 2 minute.</p>
        <v-progress-linear color="success" indeterminate rounded height="6" class="mb-6" />
      </template>

      <!-- T2 CTA (T1 only, shown once interpretation is ready) -->
      <template v-if="profileState === 'T1' && t1Interpretation">
        <v-divider class="my-6" />
        <v-alert v-if="pendingInvites.length" type="info" variant="tonal" rounded="lg" class="mb-4">
          {{ inviteHeadsUp }}
        </v-alert>
        <p class="text-medium-emphasis mb-4">{{ t1CTABody }}</p>
        <v-btn color="primary" size="large" to="/profile/t2" append-icon="mdi-arrow-right">
          Continuă profilul
        </v-btn>
      </template>

      <!-- T2 interpretation (T2 only) -->
      <template v-if="profileState === 'T2'">

        <!-- Atașament -->
        <v-divider class="my-6" />
        <div id="section-attachment" class="section-heading mb-1">
          <h2 class="text-subtitle-1 font-weight-bold">3. Atașament</h2>
          <a href="#top" class="back-to-top text-caption" @click.prevent="scrollTo('top')">↑ sus</a>
        </div>
        <p class="text-body-2 text-medium-emphasis mb-4">Cum te raportezi la intimitate și apropiere în relații. Stilul tău de atașament reflectă nevoia de securitate și confortul cu dependența emoțională.</p>

        <template v-if="t2Loading">
          <p class="text-medium-emphasis mb-2">Se generează... Generarea ar putea dura până la 2 minute.</p>
          <v-progress-linear color="primary" indeterminate rounded height="6" />
        </template>
        <template v-else-if="t2Interpretation">
          <v-card rounded="lg" elevation="1" class="pa-5 mb-4">
            <div class="d-flex align-center justify-space-between mb-1" style="cursor: pointer" @click="sectionLegend.attachment = !sectionLegend.attachment">
              <div class="text-overline text-medium-emphasis">Harta atașamentului</div>
              <v-icon size="small" :icon="sectionLegend.attachment ? 'mdi-help-circle' : 'mdi-help-circle-outline'" />
            </div>
            <v-expand-transition>
              <div v-if="sectionLegend.attachment" class="text-caption text-medium-emphasis mb-3">
                Stilul tău de atașament, în relații apropiate, este măsurat pe două axe independente.
                <ul style="padding-left: 1.25rem;">
                  <li><span class="font-weight-bold">Anxietate</span> — teama de respingere sau abandon, nevoia de reasigurare</li>
                  <li><span class="font-weight-bold">Evitare</span> — disconfortul față de intimitate și dependență emoțională</li>
                </ul>
                <div class="mt-2">Cele 4 tipuri de atașament posibile sunt:</div>
                <ul style="padding-left: 1.25rem;">
                  <li><span class="font-weight-bold">Securizant</span> — Confortabil cu intimitatea și autonomia; relații stabile și echilibrate.</li>
                  <li><span class="font-weight-bold">Evitant</span> — Valorizează autonomia și independența; minimizează nevoile emoționale.</li>
                  <li><span class="font-weight-bold">Anxios / Preocupat</span> — Caută apropierea intensă; teamă de abandon; hipersensibil la semnalele relaționale.</li>
                  <li><span class="font-weight-bold">Evitant anxios</span> — Dorește intimitatea dar se teme de ea; ambivalent față de apropiere.</li>
                </ul>
              </div>
            </v-expand-transition>
            <div style="height: 280px;">
              <AttachmentChart
                :anxiety="{ score: t2Interpretation.local.attachment.anxiety }"
                :avoidance="{ score: t2Interpretation.local.attachment.avoidance }"
              />
            </div>

            <div class="d-flex align-center flex-wrap gap-2">
              <span class="text-caption text-medium-emphasis">Tipul tău de atașament</span>  
              <v-chip size="small" color="primary" variant="tonal">{{ t2Interpretation.local.attachment.style.label }}</v-chip>
            </div>

            <div class="text-subtitle-1 font-weight-medium mt-4 mb-1">{{ t2Interpretation.local.attachment.style.label }}</div>
            <p class="text-body-2 text-medium-emphasis">{{ t2Interpretation.local.attachment.style.description }}</p>
          </v-card>
          <v-card v-if="t2Interpretation.ai?.attachment" rounded="lg" elevation="1" class="pa-5" style="position: relative; overflow: hidden;">
            <BotanicalIcon name="sprig" :size="64" :opacity="0.18" :rotate="20"
              style="position: absolute; top: 8px; right: 8px; color: rgb(var(--v-theme-primary));" />
            <div class="text-overline text-medium-emphasis mb-2">Atașament · Interpretare</div>
            <div class="text-body-2 md-content" v-html="md(t2Interpretation.ai.attachment)"></div>
          </v-card>
        </template>
        <template v-else-if="t2Failed">
          <v-alert type="error" variant="tonal" class="mb-4">
            Generarea profilului a eșuat.
          </v-alert>
          <v-btn
            color="primary"
            variant="tonal"
            prepend-icon="mdi-refresh"
            :loading="t2Retrying"
            @click="retryInterpretation('t2')"
          >
            Încearcă din nou
          </v-btn>
        </template>
        <template v-else>
          <p class="text-medium-emphasis mb-2">Se procesează profilul tău... Generarea ar putea dura până la 2 minute.</p>
          <v-progress-linear color="primary" indeterminate rounded height="6" />
        </template>

        <!-- Comunicare — appears once data is loaded (same loading cycle as Atașament) -->
        <template v-if="t2Interpretation">
          <v-divider class="my-6" />
          <div id="section-comunicare" class="section-heading mb-1">
            <h2 class="text-subtitle-1 font-weight-bold">4. Comunicare</h2>
            <a href="#top" class="back-to-top text-caption" @click.prevent="scrollTo('top')">↑ sus</a>
          </div>
          <p class="text-body-2 text-medium-emphasis mb-4">Cum exprimi emoțiile, cum gestionezi dezacordurile și cum te reconectezi după conflict. Stilul tău de comunicare influențează calitatea oricărei relații.</p>

          <!-- D1: Expresiv / Internalizant -->
          <v-card rounded="lg" elevation="1" class="pa-5 mb-4">
            <div class="d-flex align-center justify-space-between mb-1" style="cursor: pointer" @click="commLegend.d1 = !commLegend.d1">
              <div class="text-overline text-medium-emphasis">Expresivitate emoțională</div>
              <v-icon size="small" :icon="commLegend.d1 ? 'mdi-help-circle' : 'mdi-help-circle-outline'" />
            </div>
            <v-expand-transition>
              <div v-if="commLegend.d1" class="text-caption text-medium-emphasis mb-3">
                Arată cât de mult preferi să verbalizezi sau să procesezi intern emoțiile dificile.
                <ul style="padding-left: 1.25rem;">
                  <li><span class="font-weight-bold">Expresiv</span> — procesezi vorbind, verbalizarea produce claritate</li>
                  <li><span class="font-weight-bold">Internalizant</span> — procesezi în tăcere, vorbești abia după ce ți-e clar</li>
                </ul>
              </div>
            </v-expand-transition>            
            <div style="height: 220px;">
              <CommunicationPieChart
                :value="t2Interpretation.local.communication.expressiveness.score"
                label-a="Expresiv"
                label-b="Internalizant"
                description-a="Procesezi vorbind — verbalizarea produce claritate"
                description-b="Procesezi în tăcere — verbalizarea vine după claritate"
              />
            </div>
            <div class="d-flex align-center gap-2 mt-3 mb-1">
              <span class="text-caption text-medium-emphasis">Stilul tău este</span>&nbsp;
              <v-chip size="small" color="primary" variant="tonal">{{ t2Interpretation.local.communication.expressiveness.label }}</v-chip>
            </div>
            <p class="text-body-2 text-medium-emphasis mt-2">{{ commDescription.expressiveness }}</p>
          </v-card>

          <!-- D2: Direct / Indirect -->
          <v-card rounded="lg" elevation="1" class="pa-5 mb-4">
            <div class="d-flex align-center justify-space-between mb-1" style="cursor: pointer" @click="commLegend.d2 = !commLegend.d2">
              <div class="text-overline text-medium-emphasis">Stil de dezacord</div>
              <v-icon size="small" :icon="commLegend.d2 ? 'mdi-help-circle' : 'mdi-help-circle-outline'" />
            </div>
            <v-expand-transition>
              <div v-if="commLegend.d2" class="text-caption text-medium-emphasis mb-3">
                Cum abordezi dezacordurile — explicit și frontal vs. pe ocolite.
                <ul style="padding-left: 1.25rem;">
                  <li><span class="font-weight-bold">Direct</span> — spui explicit ce te deranjează, preferi claritate chiar dacă e incomod</li>
                  <li><span class="font-weight-bold">Indirect</span> — eviți confruntarea deschisă, cauți rezolvarea pe ocolite</li>
                </ul>
              </div>
            </v-expand-transition>
            <div style="height: 220px;">
              <CommunicationPieChart
                :value="t2Interpretation.local.communication.directness.score"
                label-a="Direct"
                label-b="Indirect"
                description-a="Spui explicit ce te deranjează, chiar dacă e incomod"
                description-b="Eviți confruntarea deschisă, cauți rezolvarea pe ocolite"
              />
            </div>
            <div class="d-flex align-center gap-2 mt-3 mb-1">
              <span class="text-caption text-medium-emphasis">Stilul tău este</span>&nbsp;
              <v-chip size="small" color="primary" variant="tonal">{{ t2Interpretation.local.communication.directness.label }}</v-chip>
            </div>
            <p class="text-body-2 text-medium-emphasis mt-2">{{ commDescription.directness }}</p>
          </v-card>

          <!-- D3: Validare / Soluție -->
          <v-card rounded="lg" elevation="1" class="pa-5 mb-4">
            <div class="d-flex align-center justify-space-between mb-1" style="cursor: pointer" @click="commLegend.d3 = !commLegend.d3">
              <div class="text-overline text-medium-emphasis">Suport oferit celorlalți</div>
              <v-icon size="small" :icon="commLegend.d3 ? 'mdi-help-circle' : 'mdi-help-circle-outline'" />
            </div>
            <v-expand-transition>
              <div v-if="commLegend.d3" class="text-caption text-medium-emphasis mb-3">
                Ce oferi altora când sunt stresați — empatie și ascultare vs. sfaturi practice.
                <ul style="padding-left: 1.25rem;">
                  <li><span class="font-weight-bold">Validare</span> — asculți și înțelegi cum se simte celălalt, oferi empatie</li>
                  <li><span class="font-weight-bold">Soluție</span> — ajuți să găsiți o soluție concretă, oferi sfaturi practice</li>
                </ul>
              </div>
            </v-expand-transition>
            <div style="height: 240px;">
              <CommunicationQuadrant
                :x="t2Interpretation.local.communication.support.solution.score"
                :y="t2Interpretation.local.communication.support.validation.score"
                x-label="Soluție"
                y-label="Validare"
                :quadrants="{ bottomLeft: 'Rezervat', topLeft: 'Empatizant', bottomRight: 'Pragmatic', topRight: 'Flexibil' }"
              />
            </div>
            <div class="d-flex align-center gap-2 mt-3 mb-1">
              <span class="text-caption text-medium-emphasis">Stilul tău este</span>&nbsp;
              <v-chip size="small" color="primary" variant="tonal">{{ COMM_LABEL[t2Interpretation.local.communication.support.label] }}</v-chip>
            </div>
            <p class="text-body-2 text-medium-emphasis mt-2">{{ commDescription.support }}</p>
          </v-card>

          <!-- D4: Demand / Withdraw -->
          <v-card rounded="lg" elevation="1" class="pa-5 mb-4">
            <div class="d-flex align-center justify-space-between mb-1" style="cursor: pointer" @click="commLegend.d4 = !commLegend.d4">
              <div class="text-overline text-medium-emphasis">Ritm de reconectare după conflict</div>
              <v-icon size="small" :icon="commLegend.d4 ? 'mdi-help-circle' : 'mdi-help-circle-outline'" />
            </div>
            <v-expand-transition>
              <div v-if="commLegend.d4" class="text-caption text-medium-emphasis mb-3">
                Ce faci după o tensiune — urmărești rezolvarea imediată sau ai nevoie de spațiu.
                <ul style="padding-left: 1.25rem;">
                  <li><span class="font-weight-bold">Inițiere</span> — presezi pentru rezolvare imediată, nu poți lăsa lucrurile în aer</li>
                  <li><span class="font-weight-bold">Retragere</span> — te retragi temporar, revii la subiect după ce te-ai liniștit</li>
                </ul>
              </div>
            </v-expand-transition>
            <div style="height: 240px;">
              <CommunicationQuadrant
                :x="t2Interpretation.local.communication.reconnection.withdraw.score"
                :y="t2Interpretation.local.communication.reconnection.demand.score"
                x-label="Retragere"
                y-label="Inițiere"
                :midpoint="2.5"
                :quadrants="{ bottomLeft: 'Evitant', topLeft: 'Pro-activ', bottomRight: 'Retras', topRight: 'Ambivalent' }"
              />
            </div>
            <div class="d-flex align-center gap-2 mt-3 mb-1">
              <span class="text-caption text-medium-emphasis">Stilul tău este</span>&nbsp;
              <v-chip size="small" color="primary" variant="tonal">{{ COMM_LABEL[t2Interpretation.local.communication.reconnection.label] }}</v-chip>
            </div>
            <p class="text-body-2 text-medium-emphasis mt-2">{{ commDescription.reconnection }}</p>
          </v-card>

          <!-- AI interpretation for communication -->
          <v-card v-if="t2Interpretation.ai?.communication" rounded="lg" elevation="1" class="pa-5" style="position: relative; overflow: hidden;">
            <BotanicalIcon name="leaf" :size="64" :opacity="0.18" :rotate="-20"
              style="position: absolute; top: 8px; right: 8px; color: rgb(var(--v-theme-primary));" />
            <div class="text-overline text-medium-emphasis mb-2">Comunicare · Interpretare</div>
            <div class="text-body-2 md-content" v-html="md(t2Interpretation.ai.communication)"></div>
          </v-card>

          <!-- First-match nudge at the bottom — mirrors the top banner for users who scroll past it -->
          <v-alert
            v-if="matches.length === 0"
            type="success"
            variant="tonal"
            rounded="lg"
            class="mt-6"
          >
            Felicitări, profilul tău este acum complet! Dacă dorești, poți trimite o cerere de compatibilitate unui coleg, amic sau partener.
            <template #append>
              <v-btn size="small" variant="tonal" color="success" to="/matches">Trimite</v-btn>
            </template>
          </v-alert>

          <!-- Pending invite reminder at the bottom — visible after reading the full profile -->
          <v-alert
            v-if="pendingInvites.length"
            type="info"
            variant="tonal"
            rounded="lg"
            class="mt-6"
          >
            {{ inviteHeadsUp }} Dacă vrei, poți vedea compatibilitatea voastră.
            <template #append>
              <v-btn size="small" variant="tonal" color="info" to="/matches">Vezi invitațiile</v-btn>
            </template>
          </v-alert>

          <!-- Profile self-rating — only once the full T2 profile is visible and not yet rated -->
          <ProfileFeedbackCard
            v-if="profileState === 'T2' && t2Interpretation && store.user?.rating == null"
            @rateSubmitted="onRateSubmitted"
          />
        </template>

      </template>

    </template>

  </v-container>
</template>

<script>
import { appStore } from '@/stores/AppStore'
import apiService from '@/services/ApiService'
import { marked } from 'marked'
import PvqChart               from '@/components/user/PvqChart.vue'
import PvqWheelChart          from '@/components/user/PvqWheelChart.vue'
import TipiChart              from '@/components/user/TipiChart.vue'
import AttachmentChart        from '@/components/user/AttachmentChart.vue'
import CommunicationPieChart  from '@/components/user/CommunicationPieChart.vue'
import CommunicationQuadrant  from '@/components/user/CommunicationQuadrant.vue'
import ProfileFeedbackCard    from '@/components/user/ProfileFeedbackCard.vue'
import BotanicalIcon          from '@/components/BotanicalIcon.vue'

// TODO: replace with i18n lookup when translation layer is added
const TIPI_LABEL = Object.freeze({
  'extraversion':        'Extraversiune',
  'agreeableness':       'Agreabilitate',
  'conscientiousness':   'Conștiinciozitate',
  'emotional-stability': 'Stabilitate emoțională',
  'openness':            'Deschidere',
})

const COMM_LABEL = Object.freeze({
  'flexible':   'Flexibil',
  'empathetic': 'Empatizant',
  'pragmatic':  'Pragmatic',
  'reserved':   'Rezervat',
  'pro-active': 'Pro-activ',
  'ambivalent': 'Ambivalent',
  'withdrawn':  'Retras',
  'avoidant':   'Evitant',
})

const HIGHER_ORDER_LABEL = Object.freeze({
  'self-transcendence': 'Autodepășire',
  'self-enhancement':   'Autopromovare',
  'openness-to-change': 'Deschidere',
  'conservation':       'Conservare',
})

const POLL_INTERVAL = parseInt(process.env.VUE_APP_POLL_INTERVAL_MS ?? '2000')
const POLL_MAX      = Math.ceil(parseInt(process.env.VUE_APP_POLL_TIMEOUT_MS ?? '120000') / POLL_INTERVAL)

export default {
  name: 'MeView',
  components: { PvqChart, PvqWheelChart, TipiChart, AttachmentChart, CommunicationPieChart, CommunicationQuadrant, ProfileFeedbackCard, BotanicalIcon },

  data() {
    return {
      TIPI_LABEL,
      COMM_LABEL,
      HIGHER_ORDER_LABEL,
      t1Loading: false,
      t1Interpretation: null,
      t1PollTimer: null,
      t1PollCount: 0,
      t1Failed: false,
      t1Retrying: false,

      t2Loading: false,
      t2Interpretation: null,
      t2PollTimer: null,
      t2PollCount: 0,
      t2Failed: false,
      t2Retrying: false,

      commLegend:    { d1: false, d2: false, d3: false, d4: false },
      sectionLegend: { values: false, bipolarProfile: false, personality: false, attachment: false },

      matches: [],
    }
  },

  computed: {
    store() {
      return appStore()
    },

    profileState() {
      return this.store.user?.profileState ?? 'T0'
    },

    // Current user's DB id — used to distinguish initiator vs recipient in match records.
    userId() {
      return this.store.user?.user_id
    },

    // CLAIMED matches where the logged-in user is the recipient (someone is waiting for their answer).
    pendingInvites() {
      return this.matches.filter(m => m.state === 'CLAIMED' && m.recipientId === this.userId)
    },

    // "Ai o invitație de la X." or "Ai invitații de la: X, Y." — empty string when no pending invites.
    inviteHeadsUp() {
      const names = this.pendingInvites.map(m => m.initiator.firstName)
      if (names.length === 1) return `Ai o invitație de la ${names[0]}.`
      return `Ai invitații de la: ${names.join(', ')}.`
    },

    // CTA body for T0 — invite-aware to lead with self-discovery, not social obligation.
    t0CTABody() {
      return this.pendingInvites.length
        ? 'Începe să-ți completezi profilul ca să-ți descoperi valorile, tipul de personalitate, stilul de atașament și de comunicare și, la final, dacă vrei, să vezi compatibilitatea voastră.'
        : 'Completează profilul psihologic pentru a-ți descoperi valorile și personalitatea.'
    },

    // CTA body for T1 — same self-discovery framing, scoped to the remaining T2 instruments.
    t1CTABody() {
      return this.pendingInvites.length
        ? 'Termină să-ți completezi profilul ca să-ți mai descoperi stilul de atașament și de comunicare și, la final, dacă vrei, să vezi compatibilitatea voastră.'
        : 'Termină să-ți completezi profilul ca să-ți descoperi stilul de atașament și de comunicare și, la final, dacă vrei, să-ți testezi compatibilitatea cu cine dorești.'
    },

    // Rule-based description per communication dimension, derived from the stored interpretation.
    commDescription() {
      const c = this.t2Interpretation?.local?.communication
      if (!c) return {}

      const EXPRESSIVENESS = {
        'Expresiv':      'Procesezi emoțiile vorbind — ai nevoie de cineva cu care să gândești cu voce tare.',
        'Internalizant': 'Procesezi intern — te clarifici singur înainte să vorbești cu ceilalți.',
        'echilibrat':    'Ești flexibil — poți procesa atât vorbind, cât și singur, în funcție de context.',
      }

      const DIRECTNESS = {
        'Direct':    'Spui direct ce te deranjează — claritatea contează mai mult decât confortul momentan.',
        'Indirect':  'Preferi să rezolvi pe ocolite — evitarea conflictului deschis este o prioritate pentru tine.',
        'echilibrat':'Adaptezi stilul la situație — poți fi direct sau indirect în funcție de context.',
      }

      const highV = c.support.validation.level === 'ridicat'
      const highS = c.support.solution.level   === 'ridicat'
      const supportDesc =
        highV &&  highS ? 'Ești un susținător complet — oferi atât empatie cât și soluții practice.' :
        highV && !highS ? 'Ești orientat spre empatie — asculți și înțelegi înainte de orice.' :
       !highV &&  highS ? 'Ești orientat spre soluții — preferi să ataci problema direct.' :
                          'Ești mai rezervat în suport — nu te grăbești să intervii.'

      const highD = c.reconnection.demand.score   >= 2.5
      const highW = c.reconnection.withdraw.score >= 2.5
      const reconnectionDesc =
         highD &&  highW ? 'Ești ambivalent — poți fi atât cel care presează cât și cel care se retrage, în funcție de situație.' :
         highD && !highW ? 'Ești pro-activ — inițiezi discuția și nu lași tensiunile neadresate.' :
        !highD &&  highW ? 'Ești retras — ai nevoie de spațiu înainte să revii la subiect.' :
                           'Ești evitant — nu presezi și nici nu te retragi activ; lași tensiunile să se rezolve de la sine.'

      return {
        expressiveness: EXPRESSIVENESS[c.expressiveness.label] ?? '',
        directness:     DIRECTNESS[c.directness.label]         ?? '',
        support:        supportDesc,
        reconnection:   reconnectionDesc,
      }
    },

    wheelChartAngle() {                                                                                                     
       const alphaRad = this.t1Interpretation?.local?.pvq?.alphaAngle                                                        
       if (alphaRad == null) return null                                                                                     
       const deg = alphaRad * 180 / Math.PI                                                                                  
       return (90 - deg + 360) % 360                                                                                         
     },
  },

  async created() {
    // Fire-and-forget — doesn't block profile rendering.
    this.loadMatches()

    if (this.profileState === 'T1' || this.profileState === 'T2') {
      await this.loadInterpretation('t1')
    }
    if (this.profileState === 'T2') {
      // Don't await — let T2 load in the background so the loading indicator
      // is visible when we scroll to the section below.
      this.loadInterpretation('t2')
    }
    const target = this.$route.query.scrollTo
    if (target) {
      // T1 content is rendered; wait one tick for the DOM to settle, then scroll.
      await this.$nextTick()
      document.getElementById(target)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  },

  beforeUnmount() {
    clearInterval(this.t1PollTimer)
    clearInterval(this.t2PollTimer)
  },

  methods: {
    // Loads all matches so pending invites can be derived client-side (same data MatchesView uses).
    async loadMatches() {
      try {
        this.matches = await apiService.getMatches()
      } catch (err) {
        console.error('MeView > loadMatches error:', err)
      }
    },


    /**
     * Load T(ier)/Test intepretation
     * @param tier can be either 't1' or 't2'
     */
    async loadInterpretation(tier) {
      const loadingKey  = `${tier}Loading`
      const dataKey     = `${tier}Interpretation`
      const timerKey    = `${tier}PollTimer`
      const countKey    = `${tier}PollCount`
      const failedKey   = `${tier}Failed`

      const get = tier === 't1'
        ? () => apiService.getT1Interpretation()
        : () => apiService.getT2Interpretation()

      this[loadingKey] = true
      this[failedKey]  = false
      try {
        const data = await get()
        this[dataKey] = data.interpretation

        if (!this[dataKey]) {
          this[timerKey] = setInterval(async () => {
            try {
              const d = await get()
              if (d.interpretation) {
                this[dataKey] = d.interpretation
                clearInterval(this[timerKey])
              } else if (++this[countKey] >= POLL_MAX) {
                clearInterval(this[timerKey])
                this[failedKey] = true
              }
            } catch (err) {
              console.error(`MeView > ${tier} poll error:`, err)
              clearInterval(this[timerKey])
              this[failedKey] = true
            }
          }, POLL_INTERVAL)
        }
      } catch (err) {
        console.error(`MeView > load ${tier} error:`, err)
        this[failedKey] = true
      } finally {
        this[loadingKey] = false
      }
    },

    // Renders Claude markdown output as HTML. Safe here because the content
    // comes from Claude, not from user input.
    // Headings are shifted down two levels (# → ###, ## → ####) so they don't
    // compete visually with the card section headers in the surrounding UI.
    md(text) {
      const shifted = text.replace(/^(#{1,4})/gm, '##$1')
      return marked(shifted)
    },

    scrollTo(id) {
      document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    },

    /**
     * Called when the user submits a profile self-rating.
     * Updates the store so the feedback card disappears in the current session.
     * @param {number} rating - the submitted rating value (1–5)
     */
    onRateSubmitted(rating) {
      this.store.setUser({ ...this.store.user, rating })
    },


    async retryInterpretation(tier) {
      const retryingKey = `${tier}Retrying`
      const countKey    = `${tier}PollCount`
      const failedKey   = `${tier}Failed`

      this[retryingKey] = true
      try {
        if (tier === 't1') await apiService.retryT1Interpretation()
        else               await apiService.retryT2Interpretation()
        this[failedKey]  = false
        this[countKey]   = 0
        await this.loadInterpretation(tier)
      } catch (err) {
        console.error(`MeView > retry ${tier} error:`, err)
      } finally {
        this[retryingKey] = false
      }
    },
  },
}
</script>

<style scoped>
/* Vuetify's global CSS reset strips margins, padding and list styles from
   all HTML elements. Since v-html content is not scoped, those defaults are
   never restored. `all: revert` lifts Vuetify's reset for every element
   inside .md-content, giving back browser defaults (paragraph spacing,
   list indentation, etc.) without re-declaring them one by one. */
.md-content :deep(*) { all: revert; }

/* TOC ordered list */
.toc {
  padding-left: 1.25rem;
  line-height: 2;
}
.toc a {
  color: inherit;
  text-decoration: underline;
  text-underline-offset: 3px;
  cursor: pointer;
}

/* Section heading row: title on the left, ↑ sus link on the right */
.section-heading {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
}
.back-to-top {
  color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity));
  text-decoration: none;
  cursor: pointer;
  white-space: nowrap;
  margin-left: 8px;
}
.back-to-top:hover {
  text-decoration: underline;
}


</style>
